import { useState, type ReactNode, type FormEvent } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, Vacio, Badge, Modal, BotonAccion, diaNombre, confirmar } from '../shared/ui.tsx';
import type { Persona, Categoria, Disponibilidad } from '../shared/tipos.ts';

export function PersonalPage(): ReactNode {
  const [busqueda, setBusqueda] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const path = `/api/personal?busqueda=${encodeURIComponent(busqueda)}&categoria_id=${categoriaFiltro}&activo=${verInactivos ? '0,1' : '1'}`;
  const { datos, cargando, error, recargar } = useApi<{ resultados: (Persona & { categorias: string })[]; total: number }>(path);
  const categorias = useApi<Categoria[]>('/api/categorias?activo=1');

  const [editando, setEditando] = useState<Persona | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);

  function alta() {
    setEditando(null);
    setMostrarForm(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-bold text-neutro-800">Personal</h2>
        <BotonAccion onClick={alta}>+ Nueva persona</BotonAccion>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          className="campo max-w-xs"
          placeholder="Buscar por nombre, CI…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select
          className="campo max-w-[200px]"
          value={categoriaFiltro}
          onChange={(e) => setCategoriaFiltro(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {categorias.datos?.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-neutro-700">
          <input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} />
          Ver inactivos
        </label>
      </div>

      {cargando ? (
        <Cargando />
      ) : error ? (
        <ErrorApi mensaje={error} onReintentar={recargar} />
      ) : datos && datos.resultados.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm border border-contorno-var">
          <table className="w-full text-sm">
            <thead className="bg-superficie-100 text-left text-neutro-800">
              <tr>
                <th className="px-4 py-3 font-semibold">Nombre</th>
                <th className="px-4 py-3 font-semibold">Categorías</th>
                <th className="px-4 py-3 font-semibold">Prioridad</th>
                <th className="px-4 py-3 font-semibold">Calif.</th>
                <th className="px-4 py-3 font-semibold">Estado</th>
                <th className="px-4 py-3"></th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {datos.resultados.map((p) => (
                <tr key={p.id} className="border-t border-contorno-var hover:bg-superficie-100/50">
                  <td className="px-4 py-3">
                    <button
                      className="font-medium text-neutro-800 hover:underline"
                      onClick={() => { setEditando(p); setMostrarForm(true); }}
                    >
                      {p.apellido}, {p.nombre}
                    </button>
                    {p.telefono && <div className="text-xs text-neutro-400">{p.telefono}</div>}
                  </td>
                  <td className="px-4 py-3 text-neutro-500">{p.categorias || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-superficie-200 text-xs font-bold text-neutro-700">
                      {p.prioridad}
                    </span>
                  </td>
                  <td className="px-4 py-3">{p.calificacion > 0 ? p.calificacion.toFixed(1) : '—'}</td>
                  <td className="px-4 py-3">
                    {p.activo ? <Badge color="verde">Activo</Badge> : <Badge color="gris">Inactivo</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.activo === 1 && (
                      <button
                        className="text-xs text-estado-error hover:underline"
                        onClick={async () => {
                          if (confirmar(`¿Dar de baja a ${p.nombre} ${p.apellido}?`)) {
                            await apiPost(`/api/personal/${p.id}`, undefined, 'DELETE');
                            recargar();
                          }
                        }}
                      >
                        Desactivar
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.activo === 1 && <BotonInvitar personaId={p.id} nombre={p.nombre} apellido={p.apellido} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Vacio texto="No hay personal que coincida con los filtros." />
      )}

      {mostrarForm && (
        <FormPersona
          persona={editando}
          categorias={categorias.datos ?? []}
          onCerrar={() => setMostrarForm(false)}
          onGuardado={() => { setMostrarForm(false); recargar(); }}
        />
      )}
    </div>
  );
}

function FormPersona({
  persona,
  categorias,
  onCerrar,
  onGuardado,
}: {
  persona: Persona | null;
  categorias: Categoria[];
  onCerrar: () => void;
  onGuardado: () => void;
}): ReactNode {
  const esNuevo = !persona;
  const [form, setForm] = useState({
    nombre: persona?.nombre ?? '',
    apellido: persona?.apellido ?? '',
    documento: persona?.documento ?? '',
    telefono: persona?.telefono ?? '',
    email: persona?.email ?? '',
    prioridad: persona?.prioridad ?? 5,
    notas: persona?.notas ?? '',
  });
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // categorías asignadas (modo edición)
  const { datos: detalle } = useApi<{ categorias: { categoria_id: string; es_principal: number }[] }>(
    persona ? `/api/personal/${persona.id}` : null,
  );
  const [catsSel, setCatsSel] = useState<Map<string, boolean>>(new Map());
  const [catPrincipal, setCatPrincipal] = useState<string>('');
  const [mostrarDisp, setMostrarDisp] = useState(false);
  const [mostrarDispositivos, setMostrarDispositivos] = useState(false);
  const [mostrarLocales, setMostrarLocales] = useState(false);

  // cuando llega el detalle, setear cats
  const [catsCargadas, setCatsCargadas] = useState(false);
  if (detalle && !catsCargadas) {
    const m = new Map<string, boolean>();
    detalle.categorias.forEach((c) => m.set(c.categoria_id, true));
    setCatsSel(m);
    const prin = detalle.categorias.find((c) => c.es_principal)?.categoria_id;
    if (prin) setCatPrincipal(prin);
    setCatsCargadas(true);
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setGuardando(true);
    try {
      const body = { ...form };
      if (esNuevo) {
        const cats = Array.from(catsSel.entries())
          .filter(([, v]) => v)
          .map(([cid]) => ({ categoria_id: cid, es_principal: catPrincipal === cid }));
        const { id } = await apiPost<{ id: string }>('/api/personal', { ...body, categorias: cats });
        void id;
      } else {
        await apiPost(`/api/personal/${persona!.id}`, body, 'PUT');
        const cats = Array.from(catsSel.entries())
          .filter(([, v]) => v)
          .map(([cid]) => ({ categoria_id: cid, es_principal: catPrincipal === cid }));
        await apiPost(`/api/personal/${persona!.id}/categorias`, { categorias: cats }, 'PUT');
      }
      onGuardado();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  function toggleCat(id: string) {
    setCatsSel((m) => {
      const n = new Map(m);
      if (n.has(id)) n.delete(id);
      else n.set(id, true);
      return n;
    });
  }

  return (
    <Modal abierto={true} titulo={esNuevo ? 'Nueva persona' : `Editar ${persona?.nombre} ${persona?.apellido}`} onCerrar={onCerrar}>
      <form onSubmit={guardar} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Nombre*</span>
            <input className="campo" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Apellido*</span>
            <input className="campo" required value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Cédula</span>
            <input className="campo" value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Teléfono</span>
            <input className="campo" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Email</span>
            <input className="campo" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Prioridad (1 = máxima)</span>
            <input className="campo" type="number" min={1} max={10} value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: parseInt(e.target.value, 10) || 5 })} />
          </label>
        </div>

        <fieldset className="rounded-xl border border-contorno-var p-3">
          <legend className="px-1 text-sm font-medium text-neutro-800">Categorías</legend>
          <div className="mt-2 space-y-1.5">
            {categorias.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={catsSel.has(c.id) || false}
                  onChange={() => toggleCat(c.id)}
                />
                <span>{c.nombre}</span>
                {catsSel.has(c.id) && (
                  <label className="ml-auto text-xs text-neutro-400">
                    <input
                      type="radio"
                      name="principal"
                      checked={catPrincipal === c.id}
                      onChange={() => setCatPrincipal(c.id)}
                    />{' '} principal
                  </label>
                )}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-neutro-800">Notas</span>
          <textarea className="campo" rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
        </label>

        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-estado-error">{err}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <BotonAccion type="button" variante="secundario" onClick={onCerrar}>Cancelar</BotonAccion>
          <BotonAccion type="submit" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</BotonAccion>
        </div>
      </form>

      {!esNuevo && (
        <>
          <button
            className="mt-4 text-sm font-medium text-neutro-700 hover:underline"
            onClick={() => setMostrarDisp(!mostrarDisp)}
          >
            {mostrarDisp ? '▾' : '▸'} Disponibilidad
          </button>
          {mostrarDisp && <DisponibilidadPersona personaId={persona!.id} />}
          <button
            className="mt-2 block text-sm font-medium text-neutro-700 hover:underline"
            onClick={() => setMostrarLocales(!mostrarLocales)}
          >
            {mostrarLocales ? '▾' : '▸'} Locales donde trabaja
          </button>
          {mostrarLocales && <LocalesPersona personaId={persona!.id} />}
          <button
            className="mt-2 block text-sm font-medium text-neutro-700 hover:underline"
            onClick={() => setMostrarDispositivos(!mostrarDispositivos)}
          >
            {mostrarDispositivos ? '▾' : '▸'} Dispositivo de confianza
          </button>
          {mostrarDispositivos && <DispositivosPersona personaId={persona!.id} />}
        </>
      )}
    </Modal>
  );
}

function LocalesPersona({ personaId }: { personaId: string }): ReactNode {
  const locales = useApi<{ cliente_ids: string[] }>(`/api/personal/${personaId}/locales`);
  const clientes = useApi<Array<{ id: string; nombre: string }>>('/api/clientes');
  const [sel, setSel] = useState<string[] | null>(null);
  const [guardando, setGuardando] = useState(false);

  const actual = sel ?? locales.datos?.cliente_ids ?? [];
  const todos = actual.length === 0;

  function toggle(id: string) {
    setSel(actual.includes(id) ? actual.filter((x) => x !== id) : [...actual, id]);
  }

  async function guardar(nuevos: string[]) {
    setGuardando(true);
    try {
      await apiPost(`/api/personal/${personaId}/locales`, { cliente_ids: nuevos }, 'PUT');
      setSel(null);
      locales.recargar();
    } catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
    finally { setGuardando(false); }
  }

  if (locales.cargando || clientes.cargando) return <Cargando />;
  if (locales.error) return <ErrorApi mensaje={locales.error} onReintentar={locales.recargar} />;

  return (
    <div className="mt-2 rounded-xl bg-superficie-100/50 p-3 text-sm">
      <p className="mb-2 text-xs text-neutro-400">
        El motor solo la convoca para estos locales, y solo estos clientes la ven al elegir personal.
      </p>
      <label className="flex items-center gap-2">
        <input type="radio" checked={todos} onChange={() => { setSel([]); void guardar([]); }} disabled={guardando} />
        <span className="font-medium">Todos los locales</span>
      </label>
      <div className="mt-1.5 space-y-1 pl-5">
        {(clientes.datos ?? []).map((c) => (
          <label key={c.id} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={actual.includes(c.id)}
              onChange={() => toggle(c.id)}
              disabled={guardando}
            />
            <span>{c.nombre}</span>
          </label>
        ))}
      </div>
      {sel !== null && sel.length > 0 && (
        <BotonAccion variante="secundario" disabled={guardando} onClick={() => void guardar(sel)}>
          {guardando ? 'Guardando…' : `Guardar (${sel.length} local${sel.length > 1 ? 'es' : ''})`}
        </BotonAccion>
      )}
    </div>
  );
}

type DispositivoConfianza = {
  id: string; device_id: string; modelo: string | null;
  estado: 'pendiente' | 'aprobado' | 'rechazado'; creado_en: string; resuelto_en: string | null;
};

function DispositivosPersona({ personaId }: { personaId: string }): ReactNode {
  const { datos, cargando, error, recargar } = useApi<DispositivoConfianza[]>(`/api/personal/${personaId}/dispositivos`);

  async function resolver(id: string, accion: 'aprobar' | 'rechazar') {
    try { await apiPost(`/api/dispositivos-confianza/${id}/resolver`, { accion }); recargar(); }
    catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
  }
  async function desvincular(id: string) {
    if (!confirmar('¿Desvincular este dispositivo? La persona va a tener que pedir aprobación de nuevo al entrar.')) return;
    try { await apiPost(`/api/dispositivos-confianza/${id}`, undefined, 'DELETE'); recargar(); }
    catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
  }

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;

  return (
    <div className="mt-2 rounded-xl bg-superficie-100/50 p-3 text-sm">
      <p className="mb-2 text-xs text-neutro-400">
        La app del celular solo funciona desde un dispositivo aprobado. El acceso web no se restringe.
      </p>
      {datos && datos.length > 0 ? (
        <ul className="space-y-1.5">
          {datos.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-contorno-var py-1.5">
              <span>
                {d.modelo ?? 'Dispositivo'} · <span className="text-xs text-neutro-400">{new Date(d.creado_en).toLocaleDateString('es-UY')}</span>
                {' '}
                {d.estado === 'aprobado' && <Badge color="verde">Aprobado</Badge>}
                {d.estado === 'pendiente' && <Badge color="amarillo">Pendiente</Badge>}
                {d.estado === 'rechazado' && <Badge color="gris">Rechazado</Badge>}
              </span>
              <span className="flex gap-2">
                {d.estado === 'pendiente' && (
                  <>
                    <button className="text-xs font-medium text-estado-ok hover:underline" onClick={() => resolver(d.id, 'aprobar')}>Aprobar</button>
                    <button className="text-xs text-estado-error hover:underline" onClick={() => resolver(d.id, 'rechazar')}>Rechazar</button>
                  </>
                )}
                {d.estado === 'aprobado' && (
                  <button className="text-xs text-estado-error hover:underline" onClick={() => desvincular(d.id)}>Desvincular</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-neutro-400">Todavía no entró a la app desde ningún dispositivo.</p>
      )}
    </div>
  );
}

function BotonInvitar({ personaId }: { personaId: string; nombre: string; apellido: string }) {
  const [link, setLink] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  async function generar() {
    setCargando(true);
    try {
      const r = await apiPost<{ token: string }>('/api/invitaciones', { persona_id: personaId });
      setLink(`${window.location.origin}/invitacion/${r.token}`);
      setCopiado(false);
    } catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
    finally { setCargando(false); }
  }

  async function copiar() {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopiado(true);
  }

  if (link) {
    return (
      <div className="flex flex-col items-end gap-1">
        <code className="max-w-[180px] truncate text-xs text-neutro-700">{link}</code>
        <button onClick={copiar} className="text-xs text-primario-400 hover:underline">
          {copiado ? '✓ Copiado' : 'Copiar link'}
        </button>
      </div>
    );
  }
  return (
    <button onClick={generar} disabled={cargando} className="text-xs text-primario-400 hover:underline">
      {cargando ? '…' : 'Invitar'}
    </button>
  );
}

function DisponibilidadPersona({ personaId }: { personaId: string }): ReactNode {
  const path = `/api/personal/${personaId}/disponibilidad`;
  const { datos, cargando, error, recargar } = useApi<Disponibilidad[]>(path);
  const [tipo, setTipo] = useState<'semanal' | 'bloqueo'>('semanal');
  const [dia, setDia] = useState('1');
  const [fecha, setFecha] = useState('');
  const [desde, setDesde] = useState('18:00');
  const [hasta, setHasta] = useState('23:59');
  const [agregando, setAgregando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function agregar(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setAgregando(true);
    try {
      await apiPost(path, {
        tipo,
        dia_semana: tipo === 'semanal' ? parseInt(dia, 10) : null,
        fecha: tipo === 'bloqueo' ? fecha : null,
        desde,
        hasta,
      });
      setFecha('');
      recargar();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error');
    } finally {
      setAgregando(false);
    }
  }

  return (
    <div className="mt-2 rounded-xl bg-superficie-100/50 p-3">
      {cargando ? (
        <Cargando texto="Cargando disponibilidad…" />
      ) : error ? (
        <ErrorApi mensaje={error} onReintentar={recargar} />
      ) : (
        <>
          {datos && datos.length > 0 ? (
            <ul className="mb-3 space-y-1 text-sm">
              {datos.map((d) => (
                <li key={d.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5">
                  <span>
                    {d.tipo === 'semanal' ? `Cada ${diaNombre(d.dia_semana ?? 0)}` : `Bloqueo ${d.fecha}`}
                    {' · '}
                    {d.desde}–{d.hasta}
                  </span>
                  <button
                    className="text-xs text-estado-error hover:underline"
                    onClick={async () => {
                      await apiPost(`${path}/${d.id}`, undefined, 'DELETE');
                      recargar();
                    }}
                  >
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-3 text-xs text-neutro-400">Sin disponibilidad declarada.</p>
          )}

          <form onSubmit={agregar} className="space-y-2 rounded-lg bg-white p-3">
            <div className="flex gap-2">
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" checked={tipo === 'semanal'} onChange={() => setTipo('semanal')} /> Semanal
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input type="radio" checked={tipo === 'bloqueo'} onChange={() => setTipo('bloqueo')} /> Bloqueo puntual
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {tipo === 'semanal' ? (
                <select className="campo" value={dia} onChange={(e) => setDia(e.target.value)}>
                  <option value="1">Lunes</option><option value="2">Martes</option>
                  <option value="3">Miércoles</option><option value="4">Jueves</option>
                  <option value="5">Viernes</option><option value="6">Sábado</option>
                  <option value="7">Domingo</option>
                </select>
              ) : (
                <input className="campo" type="date" required value={fecha} onChange={(e) => setFecha(e.target.value)} />
              )}
              <div className="flex gap-2">
                <input className="campo" type="time" value={desde} onChange={(e) => setDesde(e.target.value)} />
                <input className="campo" type="time" value={hasta} onChange={(e) => setHasta(e.target.value)} />
              </div>
            </div>
            {err && <p className="text-xs text-estado-error">{err}</p>}
            <BotonAccion type="submit" variante="secundario" disabled={agregando} className="w-full text-sm">
              {agregando ? 'Agregando…' : '+ Agregar'}
            </BotonAccion>
          </form>
        </>
      )}
    </div>
  );
}